/* eslint-disable no-use-before-define */
import { ReactNode } from "react";
import {
  ItemOfferDetails,
  LocalizedString,
  OfferUIDetails,
} from "../services/redux/types/CartLoyaltyV2Types";
import CartItem from "./CartItem";
import {
  CartItemStatus,
  DeliveryOptions,
  HeaderEnum,
  MAIN_OFFER_TYPE,
  MENU_STYLE,
  OFFER_CATEGORY,
  OnlinePaymentProvider,
  OrderTypesEnum,
  OrderTypesKeysEnum,
  PAYMENT_METHOD,
  PAYMENT_PROVIDER_ENUM,
  PagesEnum,
  PayTypeEnum,
  RESTAURANT_IMAGE_TYPE,
  ROUNDING_ENUM,
  ReceiptItemStateEnum,
  ReceiptSourceEnum,
} from "./enums";
import { SuccessReportTypes } from "./ErrorTypes";
import LoyaltyOffer from "./LoyaltyOfferClass/LoyaltyOffer";

export interface OrganizationCustomer {
  id: number;
  mail: string;
  verified: boolean;
  key: string;
  organization: Organization | null;
}

type OrganizationConfigType = {
  chainedOffersConfig?: {
    maxNumberOfUsesPerPeriod: number;
    periodInDays: number;
  };
  hideInMenuSidebar?: boolean;
};

export interface Organization {
  id: number;
  name: string;
  domain: string;
  logo: string;
  slug: string;
  colors: any;
  config?: OrganizationConfigType;
}

export type PaidItem = {
  count: number;
  id: number;
  price: number;
  feedback: number | null;
  state: ReceiptItemStateEnum;
  note: string;
  subItem: SubItemType;
  addonList: ReceiptSubItemAddon[];
  isBundleItem?: boolean;
  discountedItemValue?: number;
};

export enum DeliveryReceiptStatusEnum {
  PENDING = "PENDING",
  ON_WAY_TO_PICKUP = "ON_WAY_TO_PICKUP",
  ON_WAY_TO_DELIVERY = "ON_WAY_TO_DELIVERY",
  DELIVERED = "DELIVERED",
  CANCELLED = "CANCELLED",
}

type DeliveryRiderInfo = {
  name: string;
  mobile: string;
};

export type DeliveryReceipt = {
  franchiseDeliveryConfig: FranchiseDeliveryConfig;
  deliveryConfigZone: DeliveryConfigZone;
  DeliveryReceiptzaddressId: number;
  deliveryConfigZoneId: number;
  deliveryCost: number;
  franchiseDeliveryConfigId: number;
  id: number;
  maxEstimatedTime: number;
  minEstimatedTime: number;
  receiptId: number;
  address: Address;
  deliveryStatus?: DeliveryReceiptStatusEnum;
  riderInfo?: DeliveryRiderInfo;
  location?: LatLng;
  eta?: string;
};

type CancelledReceipt = {
  createdAt: Date;
  id: number;
  reason: string;
  receiptId: number;
  state: string;
};

export type PaidReceipt = {
  id: number;
  receiptItems: PaidItem[];
  tips: number;
  orderType: OrderTypesKeysEnum;
  paid_price: number;
  created_at: Date | string;
  last_notified_at: Date | string;
  completedAt: Date | string;
  paid_at: Date | string | null;
  discountedValue?: number;
  state: ReceiptItemStateEnum;
  prepTime: number;
  updated_at: Date | string;
  subtotal: number;
  customerPackageOffer?: CustomerPackageOffer;
  confirmedAt: Date | string | null;
  pendingAt: Date | string | null;
  paymentMethod: PAYMENT_METHOD;
  deliveryCosts?: number;
  vat?: number;
  service?: number;
  deliveryReceipt?: DeliveryReceipt;
  feedback?: Feedback;
  franchise?: Franchise;
  displayedReceiptId?: number;
  cancelledReceipt?: CancelledReceipt;
  scheduledReceipt?: ScheduledReceipt;
  customerId: number;
  note?: string | null;
  receiptExtras?: ReceiptExtras;
  source?: ReceiptSourceEnum;
  source_type?: string;
  pointingTransactions?: PointingTransaction[];
};

type PointingTransaction = {
  points: number; // positive = earned, negative = redeemed
};

export type CustomerPackageOffer = {
  id: number;
  packageOffer: OfferType;
};

type ModalOptions = {
  preventBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  componentProps?: {};
  modalStyle?: {};
};

export type ModalState = {
  isOpen: boolean;
  component: ReactNode | null;
  options: ModalOptions;
};

export type ItemOfferV1Type = {
  id: number;
  offerType: string;
  offerValue: string;
  startedTime: string;
  endedTime: string;
};

export type AddonGroupSubItemType = {
  customFranchiseAddonGroupSubItemPrices?: customPrice[];
  nestedCustomFranchiseAddonGroupSubItemPrices?: customPrice[];
  id: number;
  basePrice: number;
  price: number;
  previousPrice: number;
  subItem: SubItemType;
  count?: number;
  uuid?: string;
};

export type AddonGroupType = {
  id: number;
  uuid?: string;
  name: string;
  nameLocalized?: string;
  minChoices: number;
  maxChoices: number;
  addonGroupSubItem: AddonGroupSubItemType[];
  displayTitle?: string;
  subItemAddonGroup?: { multiMax?: boolean }[];
};

export enum OFFER_TYPE {
  PERCENTAGE = "percentage",
}

type customPrice = { price: number; franchiseId: number };

/**
 * Item tag configured in Admin — a short free-form label ("New",
 * "Best seller", …) shown as a chip on the menu card and the item page. An
 * item carries at most one, but the menu API returns them as an ordered list.
 */
// @ts-prune-ignore-next
export type ItemTagType = {
  id: number;
  label: LocalizedString;
};

/**
 * Nutrition facts shown on the item page only. Both are optional and
 * independent: an item can have calories without allergens and vice versa.
 * Variants (sub-items) may override either — a small combo and a large combo
 * do not have the same calorie count.
 */
export type ItemNutritionType = {
  calories?: number | null;
  /** Free-form allergen list ("gluten & lactose"), localized in Admin. */
  allergens?: LocalizedString | null;
};

export type SubItemType = ItemNutritionType & {
  id?: number;
  /** Menu uuid (matches loyalty entityId and cart orderLine.subItemId). */
  uuid?: string;
  name: string;
  nameLocalized?: string;
  fullNameLocalized?: string;
  fullName?: string;
  price: number;
  basePrice: number;
  previousPrice: number;
  itemId?: number;
  item?: ItemType;
  allAddonGroups?: AddonGroupType[];
  deletedAt?: string;
  customFranchiseSubItemPrices: customPrice[];
  customVatPercentage?: number;
};

export type ItemDetailsType = {
  id: number;
  item_name: string;
  img_url: string;
  originalPrice: number;
  customVatPercentage?: number;
  /** Parent item uuid for loyalty / OrderCartV2 itemId. */
  uuid?: string;
};

type MenuTimeBoundAvailability = {
  id: number;
  startTime: string;
  endTime: string;
  enabled: boolean;
};

export type TimeBoundAvailabilityData = {
  isAvailable: boolean;
  fullMessage?: string;
  availabilityMessage?: string;
};

export type ItemType = ItemNutritionType & {
  id: number;
  /** Menu uuid (loyalty item-level entityId). */
  uuid?: string;
  tags?: ItemTagType[];
  item_name: string;
  itemTimeBoundAvailabilities: MenuTimeBoundAvailability[];
  timeBoundAvailabilityData: TimeBoundAvailabilityData;
  nameLocalized?: string;
  img_url: string;
  description: string;
  descriptionLocalized?: string;
  offer: ItemOfferV1Type | null;
  itemHasOptions: boolean;
  subItems: SubItemType[];
  isFreeItem?: boolean;
  version: number;
  excludeFromOffers?: boolean;
  restaurantId: number;
  deletedAt?: string;
  /** false when the item has been pulled from the menu — not re-orderable. */
  isMenuItem?: boolean;
  customDeliveryTime?: number;
  itemCategories?: { order: number }[];
  customVatPercentage?: number;
  allImageUrls?: string[];
};

type Feedback = {
  id: number;
  numberOfStars: number;
  feedback: string;
};

export type CategoryType = {
  id: string;
  /** Menu uuid (matches loyalty category entityId). */
  uuid?: string;
  category_name: string;
  categoryTimeBoundAvailabilities: MenuTimeBoundAvailability[];
  timeBoundAvailabilityData: TimeBoundAvailabilityData;
  nameLocalized?: string;
  itemCategories: ItemCategory[];
  restaurantId: number;
};

export type ItemCategory = {
  order: number;
  item: ItemType;
};

export enum PreferredCountryEnum {
  EG = "eg",
  CA = "ca",
  SA = "sa",
  UAE = "ae",
  KW = "kw",
  BH = "bh",
}

export enum INVERTED_SECTIONS {
  SUB_ITEM_DETAILS = "subItemDetails",
  PREVIOUS_ORDER = "previousOrder",
  CURRENT_ORDER = "currentOrder",
  SELECT_ORDER_INFO = "selectOrderInfo",
  FLASH_OFFERS = "flashOffers",
  MODAL_WRAPPER = "modalWrapper",
  DELIVERY_SELECTION_DRAWER = "deliverySelectionDrawer",
  DELIVERY_BAR = "deliveryBar",
  SEARCH_DRAWER = "searchDrawer",
  SCHEDULE_SCREEN_CONTAINER = "scheduleScreenContainer",
  ADDRESS_SCREEN_CONTAINER = "addressScreenContainer",
  PAY_SCREEN_CONTAINER = "payScreenContainer",
  UPSELLING_ITEMS = "upsellingItems",
  ITEM_CART = "itemCart",
  SELECT_SCHEDULE_TIME_DRAWER = "selectScheduleTimeDrawer",
}

type ColorsType = {
  theme: string;
  primary: string;
  secondary: string;
  navBarBgColor?: string;
  navBarIconsColor?: string;
  text?: string;
  invertedSections?: INVERTED_SECTIONS[];
};

export enum RESTAURANT_IMAGE_SOURCE {
  MOBILE = "MOBILE",
  WEB = "WEB",
}

export type RestaurantImage = {
  url: string;
  type: RESTAURANT_IMAGE_TYPE;
  aspectRatio: string;
  itemId?: number;
  source?: RESTAURANT_IMAGE_SOURCE;
};

export type Restaurant = {
  partnerRestaurants: Restaurant[];
  id: number;
  name: string;
  slug: string;
  color: string;
  slogan: string;
  preferredCountry: PreferredCountryEnum;
  backgroundImgUrl: string;
  colors: ColorsType;
  logoUrl: string;
  fbid: number;
  fbDomainVerificationId: string;
  google_tags_manager: string;
  vatPercentage: number;
  servicePercentage: number;
  vat: number;
  service: number;
  currencyRoundingMethod: ROUNDING_ENUM;
  currencyRoundingLevel: number;
  franchises: Franchise[];
  mainFranchise: Franchise;
  menuVersion: number;
  s3BucketName: string;
  customDomain: string;
  config: (RestaurantConfig & ColorsType) | null;
  type: RestaurantTypeEnum;
  restaurantImages?: RestaurantImage[];
  timeZone: string;
  mainFranchiseId: number;
};

export enum RestaurantTypeEnum {
  QUICK_SERVE = "QUICK_SERVE",
  COFFEE_SHOP = "COFFEE_SHOP",
  ECOMMERCE = "ECOMMERCE",
}

export type RestaurantConfig = {
  qrcodeAutoSelect?: boolean;
  settleBilleAndCompleteInOne?: boolean;
  disablePointingInHomeScreen?: boolean;
  disableOffersSliderInHomeScreen?: boolean;
  menuStyle?: MENU_STYLE;
  detectNearestBranchLocation?: boolean;
  hasMobileApp?: boolean;
  cashPaymentPopupConfirmationTipScreen?: boolean;
  cashPaymentPopupConfirmationPayScreen?: boolean;
  cashPaymentPopupConfirmation?: boolean;
  autoLocationDetection?: boolean;
  scheduledPickupTimeInterval?: ScheduledPickupTimeInterval;
  ignoreNameFormats?: boolean;
  itemUpselling?: boolean;
  addressFormAutoFill?: boolean;
  loadingStateSolution?: boolean;
  alternatePercentageIcon?: boolean;
  hideOrderNumber?: boolean;
  signUpFeature?: boolean;
  useMapsAlternative?: boolean;
  enableInternalMaps?: boolean;
  applyDiscountAfterVat?: boolean;
  calculateVatExcludingServiceFee?: boolean;
  displayFranchiseMenu?: boolean;
  showDownloadAppPopup?: ShowDownloadAppPopup;
  hideVat?: boolean;
  // When VAT is 0, render the VAT line as "Incl." instead of a 0 amount.
  // Ignored when `hideVat` is on, which drops the line entirely.
  showVatIncludedWhenZero?: boolean;
  enableNewDeliveryExperience?: boolean;
  pricesExclVatGuestLine?: boolean;
  // Master switch for the calorie count / allergen line on the item page.
  // When off the section never renders, whatever the menu data holds.
  itemCaloriesAllergensDisplay?: boolean;
  hasOfflineMode?: {
    showQrCode?: boolean;
  };
  loyaltyCard?: boolean;
  noOnlineUrl?: boolean;
  useRestaurantNameInMenuItems?: boolean;
  useLoyaltyService?: boolean;
  // Sign-up / profile field flags.
  // Each is independently optional; treat absence as false.
  hasBirthdateField?: boolean;
  hasGenderField?: boolean;
  hasEmailField?: boolean;
  hasConferenceFields?: boolean;
  hasWelcomeDrawerV3?: boolean;
  // Dine-in table number capture on the checkout screen. Presence of the object
  // means the field is shown for DINE_IN orders; `mandatory` blocks submitting
  // the order while it is empty.
  dineInTableNumber?: { mandatory?: boolean };
  // Cloudflare Turnstile. Presence of a `siteKey` means Turnstile is enabled
  // for this restaurant and must be solved before sending an OTP.
  cloudflareTurnstile?: { siteKey: string };
};

// @ts-prune-ignore-next
export type ShowDownloadAppPopup = {
  downloadAppPopupImage: string;
  downloadAppPopupInterval?: number;
};

export enum RestaurantConfigEnum {
  ADMIN_MENU_MANAGEMENT = "adminMenuManagement",
  HAS_MOBILE_APP = "hasMobileApp",
  CASH_PAYMENT_POPUP_CONFIRMATION_TIP_SCREEN = "cashPaymentPopupConfirmationTipScreen",
  CASH_PAYMENT_POPUP_CONFIRMATION_PAY_SCREEN = "cashPaymentPopupConfirmationPayScreen",
  CART_LOYALTY_V2 = "cartLoyaltyV2",
  ITEM_UPSELLING = "itemUpselling",
  SIGN_UP_FEATURE = "signUpFeature",
  SETTLE_BILL_AND_COMPLETE_IN_ONE = "settleBilleAndCompleteInOne",
  DISPLAY_FRANCHISE_MENU = "displayFranchiseMenu",
  HIDE_VAT = "hideVat",
  SHOW_VAT_INCLUDED_WHEN_ZERO = "showVatIncludedWhenZero",
  SHOW_DOWNLOAD_APP_POPUP = "showDownloadAppPopup",
  ENABLE_NEW_DELIVERY_EXPERIENCE = "enableNewDeliveryExperience",
  HAS_OFFLINE_MODE = "hasOfflineMode",
  LOYALTY_CARD = "loyaltyCard",
  NO_ONLINE_URL = "noOnlineUrl",
  HAS_BIRTHDATE_FIELD = "hasBirthdateField",
  HAS_GENDER_FIELD = "hasGenderField",
  HAS_EMAIL_FIELD = "hasEmailField",
  HAS_CONFERENCE_FIELDS = "hasConferenceFields",
  USE_LOYALTY_SERVICE = "useLoyaltyService",
  HAS_WELCOME_DRAWER_V3 = "hasWelcomeDrawerV3",
  DINE_IN_TABLE_NUMBER = "dineInTableNumber",
}

export type allPricesType = {
  subTotal: number;
  subTotalWithDiscount?: number;
  discount?: number;
  vat?: number;
  service?: number;
  tips?: number;
  deliveryFees?: number;
  paidPrice: number;
};

export type AnalyticsExtraInfo = {
  payWithPointsQuickAction?: string;
  rewardId?: number | string;
  qrcode?: string;
  itemId?: number;
  addonGroupId?: number;
  subitemId?: number;
  categoryId?: number;
  state?: string;
  Screen?: string;
  correct?: boolean;
  chosenLang?: string;
  phoneNumber?: string;
  inputField?: string;
  address?: any;
  additionalDetails?: any;
  franchiseSlug?: string;
  login?: boolean;
  franchiseId?: number;
  customReferrer?: string;
  offerId?: number | string;
  offerType?: string;
  offerSystem?: string;
  orderType?: OrderTypesEnum;
  searchQuery?: string;
  searchResult?: string[];
  pinLocation?: {
    lat: number;
    lng: number;
  };
  tableNumber?: number;
  waiterName?: string;
  host?: string;
};

export type LatLng = {
  lng: number;
  lat: number;
};

type DeliveryDetailsType = {
  minEstimatedTime: number;
  maxEstimatedTime: number;
  deliveryFees: number;
  deliveryRadius: number;
  lng: number;
  lat: number;
  isFoodCourt?: boolean;
  deliveryArea?: LatLng[];
};

export type activateAddressColumns = {
  area?: boolean;
  street_name?: boolean;
  building_number?: boolean;
  floor_number?: boolean;
  apartment_number?: boolean;
  additional_details?: boolean;
};

type DeliveryZone = {
  name: string;
  polygon: number[][];
};

export type DeliveryConfigZone = {
  deliveryZone: DeliveryZone;
  minEstimatedTime: number;
  maxEstimatedTime: number;
  deliveryFees: number;
  deliveryZoneId: number;
};

export type FranchiseDeliveryZoneConfig = {
  scheduledOrderNowCutoff?: {
    from: string;
    to: string;
  };
  scheduledOrderIftarCutoffMins?: number;
};

export type DeliveryType = {
  activeColumns: activateAddressColumns;
  additional_details: boolean;
  apartment_number: boolean;
  area: boolean;
  building_number: boolean;
  floor_number: boolean;
  street_name: boolean;
  completedStatusTextPayScreen: string;
  completedStatusTitlePayScreen: string;
  displayName: string;
  id: number;
  placeholderCartScreen: string;
  titleCartScreen: string;
};

type DeliveryExtraConfiguration = {
  deliveryServicePercentageFees?: boolean;
};

export type FranchiseDeliveryConfig = {
  id: number;
  acceptsCashPayment: boolean;
  acceptsOnlinePayment: boolean;
  deliveryConfigZones: DeliveryConfigZone[];
  deliveryFees: number;
  deliveryType: DeliveryType;
  location: LatLng;
  maxEstimatedTime: number;
  minEstimatedTime: number;
  showOutside: boolean;
  usesMap: boolean;
  extraConfig?: DeliveryExtraConfiguration;
};

export type ScheduledOrderDetailsType = {
  hasScheduledOrder?: boolean;
  scheduledPickupTimeInterval?: ScheduledPickupTimeInterval;
  scheduledOrderIftarCutoffMins?: number;
  scheduledOrderNowCutoff?: {
    from: string;
    to: string;
  };
  scheduledTodayTab?: boolean;
  scheduledTomorrowTab?: boolean;
  scheduledAfterTomorrowTab?: boolean;
  scheduledOrderTypes?: OrderTypesEnum[];
};

export type FranchiseConfig = {
  dineIn?: PaymentConfigType;
  takeOut?: PaymentConfigType;
  delivery?: PaymentDeliveryConfigType;
  deliveryV2?: FranchiseDeliveryConfig[];
  offersPopup?: boolean;
  hasFeedback?: boolean;
  scheduledOrder?: ScheduledOrderDetailsType;
  qrcodeAutoSelect?: boolean;
  disableTips?: boolean;
  hasNoSkipBtn?: boolean;
  dineInOnlyFromQrCode?: boolean;
  selfService?: SelfServiceConfigType;
  roboostDelivery?: boolean;
  loyaltyRevamp?: boolean;
  selfServiceWithoutSignIn?: boolean;
  catering?: boolean;
  forFeatureShowcase?: boolean;
  signUpForm?: { hideEmailField?: boolean };
  poweredByPrepitLogo?: boolean;
  onlyAccessibleFromDirectLink?: boolean;
};

type ScheduledPickupTimeInterval = {
  scheduledPickupTimeInterval?: number;
};

type SelfServiceConfigType = {
  // NOT USED
  mobile?: DisablePaymentMethods;
  tablet?: DisablePaymentMethods;
  desktop?: DisablePaymentMethods;

  // USED
  sessionPopupTimeOutDisplay?: number;
  loggedInSessionPopupTimeOutDisplay?: number;
  sessionPopupTimeOutLogout?: number;
};

type DisablePaymentMethods = {
  disableOnline?: boolean;
  disableCash?: boolean;
  disablePos?: boolean;
  roboostDelivery?: boolean;
};

type PaymentConfigType = {
  pos?: boolean;
  online?: boolean;
  cash?: boolean;
  prepOnPay?: boolean;
  customOrderTypeName?: string;
  localizedCustomOrderTypeName?: string;
  cartScreenCustomOrderTypeName?: string;
  localizedCartScreenCustomOrderTypeName?: string;
};

type PaymentDeliveryConfigType = PaymentConfigType & {
  minEstimatedTime: number;
  maxEstimatedTime: number;
  deliveryFees: number;
  deliveryRadius?: number;
  lng: string;
  lat: string;
  isFoodCourt?: boolean;
  deliveryArea?: LatLng[];
  buttonTitleTableScreen?: string;
  completedStatusTitlePayScreen?: string;
  completedStatusTextPayScreen?: string;
};

// export type UniquenessOutputType = {
//   unique: boolean;
//   itemIndex: number;
// };

export type cartNestedSubItemAddonsType = {
  id: number;
  count: number;
};

// ts-prune-ignore-next
export type cartSubItemAddonsType = {
  id: number;
  count: number;
  nestedAddons?: cartNestedSubItemAddonsType[]; // todo make it required
};

export type OrderItem = {
  note: string;
  count: number;
  state: CartItemStatus;
  receiptItemId?: number;
  subItemId: number;
  addons: cartSubItemAddonsType[];
  price?: number;
  isBundleItem?: boolean;
  isBuyItem?: boolean;
  isFreeItem?: boolean;
  version?: number;
  discountedItemPercentage?: number;
  excludeFromOffers?: boolean;
  isOfferItem?: boolean;
  priceWithoutOffer?: number;
};

export type Order = {
  orderId: number | null;
  price: number;
  items: OrderItem[];
  tableNumber: number;
  orderType: OrderTypesEnum;
  note?: string;
};

export type OrderItemList = {
  name: string;
  addonList?: ReceiptSubItemAddon[];
  count: number;
  totalPrice: number;
  ids: number[];
  nameLocalized?: string;
  isFreeItem?: boolean;
  discountedItemValue?: number;
  isBundleItem?: boolean;
};

export type HeaderProps = {
  components: HeaderEnum[];
  backLink: string | null;
  currentPage: PagesEnum | null;
  dynamicPagination: string[] | null;
  display: boolean;
  restaurantInfo?: any;
  testId?: string;
  removeTipPagination?: boolean;
  title?: string;
  primaryBgColor?: string;
};

export type FooterProps = {
  text?: string;
  price: number;
  link?: PagesEnum | null;
  click?: any;
  display?: boolean;
  isLoading?: boolean;
  priceWithoutVat?: boolean;
  itemView?: boolean;
  isDisabled?: boolean;
  testId?: string;
  priceLabel?: string;
  wrapperCssClasses?: string;
  addOffer?: boolean;
};

export enum OFFER_SYSTEM {
  LOYALTY_PACKAGE_OFFER = "LOYALTY_PACKAGE_OFFER",
  FIRST_TIME_OFFER = "FIRST_TIME_OFFER",
  ORGANIZATION_OFFER = "ORGANIZATION_OFFER",
  POINTING_OFFER = "POINTING_OFFER",
  MANUAL_ASSIGNED_OFFER = "MANUAL_ASSIGNED_OFFER",
}

export type OfferType =
  | FreeItemOfferType
  | FixedAmountOfferType
  | PercentageAmountOfferType
  | DiscountedItemOfferType
  | MultiplierOfferType
  | BundleOfferType
  | BogoOfferType;

export type OfferTypeV2 = AmountOfferType | ItemOfferType | BogoOfferType;
// | BundleOfferType;

export type PackageOfferExtras = {
  customUIMessageEn?: string;
  customUIMessageAr?: string;
};

type BaseOfferType<T> = {
  id: number;
  offerSystem: OFFER_SYSTEM;
  offerType: T;
  offerCategory: OFFER_CATEGORY;
  expiryDate?: Date;
  details: {
    id: number;
    minSpend: number;
  };
  orderTypes: OrderTypesKeysEnum[];
  remainingToEligiableOfferValue?: number;
  milestonePoints?: number;
  extras?: PackageOfferExtras;
  uiDetails?: OfferUIDetails;
  isCartV2?: boolean;
  isSubOffer?: boolean;
};

export type FreeItemOfferDetailsType =
  BaseOfferType<MAIN_OFFER_TYPE.FREE_ITEM>["details"] & {
    subItem: SubItemType;
    subItems: SubItemType[];
    count: number;
  };

export type FreeItemOfferType = BaseOfferType<MAIN_OFFER_TYPE.FREE_ITEM> & {
  details: FreeItemOfferDetailsType;
};

export type DiscountedItemOfferDetailsType =
  BaseOfferType<MAIN_OFFER_TYPE.DISCOUNT_ITEM>["details"] & {
    subItem: SubItemType;
    subItems: SubItemType[];
    value: number;
  };

export type DiscountedItemOfferType =
  BaseOfferType<MAIN_OFFER_TYPE.DISCOUNT_ITEM> & {
    details: DiscountedItemOfferDetailsType;
  };

export type AmountOfferType = BaseOfferType<MAIN_OFFER_TYPE | string> & {
  details: BaseOfferType<MAIN_OFFER_TYPE | string>["details"];
};

export type ItemOfferType = BaseOfferType<MAIN_OFFER_TYPE | string> & {
  details: ItemOfferDetails;
};

export type FixedAmountOfferDetailsType =
  BaseOfferType<MAIN_OFFER_TYPE.FIXED_AMOUNT>["details"] & {
    value: number;
  };

type FixedAmountOfferType = BaseOfferType<MAIN_OFFER_TYPE.FIXED_AMOUNT> & {
  details: FixedAmountOfferDetailsType;
};

export type PercentageAmountOfferDetailsType =
  BaseOfferType<MAIN_OFFER_TYPE.PERCENTAGE_AMOUNT>["details"] & {
    value: number;
    cap: number;
    /** When true, discount applies to delivery fees (e.g. free delivery offers). */
    applyToDelivery?: boolean;
  };

export type PercentageAmountOfferType =
  BaseOfferType<MAIN_OFFER_TYPE.PERCENTAGE_AMOUNT> & {
    details: PercentageAmountOfferDetailsType;
  };

export type MultiplierOfferDetailsType =
  BaseOfferType<MAIN_OFFER_TYPE.MULTIPLIER>["details"] & {
    multiplier: number;
    from?: string;
    to?: string;
  };

export type MultiplierOfferType = BaseOfferType<MAIN_OFFER_TYPE.MULTIPLIER> & {
  details: MultiplierOfferDetailsType;
};

export type BundleOfferDetailsType =
  BaseOfferType<MAIN_OFFER_TYPE.BUNDLE>["details"] & {
    price: number;
    name: string;
    packageBundleOfferSubItems: {
      packageBundleOfferId: number;
      subItemId: number;
      count: number;
      subItem: SubItemType;
    }[];
    isBundleItem: boolean;
    value: number;
  };

type BundleOfferType = BaseOfferType<MAIN_OFFER_TYPE.BUNDLE> & {
  details: BundleOfferDetailsType;
};

export type BogoOfferDetailsType =
  BaseOfferType<MAIN_OFFER_TYPE.BOGO>["details"] & {
    name?: string;
    nameLocalized?: string;
    buySubItem: SubItemType;
    buySubItems: SubItemType[];
    // getPackageOffer: FreeItemOfferType | DiscountedItemOfferType;
    getPackageOffer: Exclude<OfferTypeV2, BogoOfferType>;
    value: number;
    selectedBuySubItem?: SubItemType;
  };

export type BogoOfferType = BaseOfferType<MAIN_OFFER_TYPE.BOGO> & {
  details: BogoOfferDetailsType;
};

export type TimedOfferDetailsType = {
  from: string;
  to: string;
};

export type confirmReceiptBodyType = {
  items: OrderItem[];
  orderType: keyof typeof OrderTypesEnum;
  tableNumber: number;
  franchiseId?: number;
  restaurantId?: number;
  franchiseSlug?: string;
  mainOfferId?: number;
  receiptNote?: string;
  paymentMethod: PAYMENT_METHOD;
  customerAddressId?: number;
  franchiseDeliveryConfigId?: number;
  totalBillPrice: number;
  promoCode?: string;
  scheduledSlotId?: number;
  numberOfGuests?: number;
  scheduledPickup?: string;
  deviceNumber?: number;
} & (
  | {
      paymentMethod:
        | PAYMENT_METHOD.CASH
        | PAYMENT_METHOD.CARD_ON_DELIVERY
        | PAYMENT_METHOD.POS
        | PAYMENT_METHOD.NONE;
    }
  | {
      paymentMethod: PAYMENT_METHOD.ONLINE_CARD;
      tips: number;
      success_url: string;
      cancel_url: string;
      cardSubtype?: string;
      maskedPan?: string;
      onlinePaymentProvider?: OnlinePaymentProvider;
      // Tap only: the payment source. On web we use Tap's hosted checkout, so we
      // send "src_all" (Tap renders every method enabled on the MID). Ignored by
      // the other providers.
      tapSource?: string;
    }
);

export type Franchise = {
  address: string;
  addressLocalized: string;
  deliveryDetails: DeliveryDetailsType;
  googleFeedback: string;
  id: number;
  locationUrl: string;
  name: string;
  outOfOperationTill: Date;
  paymentProvider: PAYMENT_PROVIDER_ENUM;
  phoneNumber: string;
  restaurantId: number;
  live: boolean;
  slug: string;
  timeZone: string;
  usesNewLinks: boolean;
  franchiseConfig: FranchiseConfig;
  isTaxInclusive?: boolean;
  prepTime?: number;
  franchiseTable?: {
    id: number;
    tableNumber: number;
    state: string;
    current_session: number;
  }[];
  location?: LatLng;
};

export type Address = {
  area: string;
  streetName: string;
  buildingNumber: string;
  floorNumber: string;
  apartmentNumber: string;
  addressTitle: string;
  additionalDetails: string;
  lng: number;
  lat: number;
  customerId: number;
  id?: number;
  deliveryType?: DeliveryType;
  deliveryTypeId?: number;
};

export type AddressPlus = {
  area: string;
  streetName: string;
  buildingNumber: string;
  floorNumber: string;
  apartmentNumber: string;
  addressTitle: string;
  additionalDetails: string;
  lng: number;
  lat: number;
  customerId: number;
  id?: number;
  isInCurrentFranchise: boolean;
  deliveryType?: DeliveryType;
  deliveryTypeId?: number;
  availableDeliveryFranchises: Franchise[];
  zoneConfig: {
    minEstimatedTime: number;
    maxEstimatedTime: number;
    deliveryFees: number;
    deliveryZoneId: number;
  } | null;
};

export type OutOfStockItem = {
  itemId: number;
  name: string;
  nameLocalized: string;
  subItemId: number;
  count: number;
};

export type outOfStockItemsDetails = {
  title: string;
  body: string;
  conditionalText?: string;
  outOfStockItems: OutOfStockItem[];
};

export type reduxCartAndLoyalty = {
  cart: {
    franchiseId: number;
    items: CartItem[];
    totalPrice: number;
    priceWithDiscount?: number;
    receiptNote?: string;
    bundlePrice?: number;
  };
  cartItemsNumber: number;
  allOrderTypesoffersArray: OfferType[];
  offersArray: OfferType[];
  redeemedOffer: OfferType | undefined;
  offerErrorReason: SuccessReportTypes | undefined;
  remainingToEligiable: number;
  isEligibleOffer: boolean;
  needsRedirect: number;
  hasRoundingMethodAndLevel: boolean;
  paymentMethod: PAYMENT_METHOD;
  tip: number;
  pointingSystem: PointingSystem | undefined;
  customDeliveryTime: number;

  promoCode: {
    name: string;
    valid: boolean;
    applied: boolean;
    packageOffer?: OfferType;
    loyaltyOffer?: LoyaltyOffer;
    referralCode?: {
      id: number;
      rewardedPoints: number;
    };
  };
  selectedScheduledOptions: {
    selectedSlot: ScheduledSlot | undefined;
    selectedScheduledOption: DeliveryOptions | undefined;
    numberOfGuests: number;
  };
  outdatedSubItems: SubItemType[];

  isFirstTimeOrder: boolean | undefined;
  isTwistOfferAvailable: boolean | undefined;
  selectedPickupTime?: string;
  outOfStockItemsDetails?: outOfStockItemsDetails;
};

export type getPageLinkProps = {
  as?: string;
  queryParams?: string[];
  orderType?: OrderTypesEnum;
  tableNumber?: number;
  refresh?: boolean;
  franchiseSlug?: string | boolean;
  invalidFranchise?: boolean;
  outOfZone?: boolean;
  qrcode?: boolean | string;
  nextPage?: PagesEnum;
  partnerLogin?: boolean;
  receiptId?: number;
  resetQueryParams?: boolean;
  payType?: PayTypeEnum;
  locale?: string;
  source?: string;
  clearTable?: boolean;
};

export type NewQueryParamsType = {
  order_type?: OrderTypesEnum;
  franchise?: string;
  table?: string;
  promo?: string;
};

export type FranchiseWithDistance = Franchise & {
  distanceFromLocationInMeters?: number;
};

export type ReceiptSubItemAddon = {
  id: number;
  count?: number;
  addonSubItem: {
    addonGroupId?: number;
    previousPrice: number;
    id: number;
    subItem: {
      previousPrice: number;
      fullName: string;
      id: number;
      item: {
        itemHasOptions: boolean;
        item_name: string;
        id: number;
      };
    };
  };
  nestedAddons: {
    id: number;
    nestedAddonSubItem: {
      addonGroupId?: number;
      previousPrice: number;
      id: number;
      subItem: {
        previousPrice: number;
        fullName: string;
        id: 4;
        item: {
          itemHasOptions: boolean;
          item_name: string;
          id: number;
        };
      };
    };
  }[];
};

export type PointingMilestone = {
  id: number;
  points: number;
  name?: string;
  nameLocalized?: string;
};

enum RESTAURANT_PACKAGE_OFFER_TYPE {
  PARTNER_DEAL_OFFER = "PARTNER_DEAL_OFFER",
  MILESTONE = "MILESTONE",
}

export type RestaurantPackageOffer = {
  packageOffer: OfferType;
  pointingMilestone?: PointingMilestone;
  id: number;
  startTime: string;
  endTime: string;
  type: RESTAURANT_PACKAGE_OFFER_TYPE;
};

export type PointingSystem = {
  restaurantPackageOffers?: RestaurantPackageOffer[];
  pointsPerOneCredit: number;
  customerCurrentPoints: number;
  pointingSystemName?: string;
  pointingSystemNameLocalized?: string;
};

export enum WAITER_REQUEST_STOCK_STATE {
  OUT_OF_STOCK = "OUT_OF_STOCK",
  OUT_OF_STOCK_TODAY = "OUT_OF_STOCK_TODAY",
  IN_STOCK = "IN_STOCK",
}

export type ScheduledSlot = {
  id: number;
  day: string;
  from: string;
  to: string;
  displayedName: string | null;
  available: boolean;
  currentAvailability: boolean;
};

export type ScheduledSlotsData = {
  TODAY?: ScheduledSlot[];
  TOMORROW?: ScheduledSlot[];
  AFTER_TOMORROW?: ScheduledSlot[];
};

export type ScheduledReceipt = {
  id: number;
  from: Date;
  to: Date;
  numberOfGuests?: number;
};

export enum NOTIFICATION_TYPES {
  REEDEMED_REFERAL = "REDEEMED_REFERRAL",
}

export type redeemedReferralNotficationDetails = {
  customerId: number;
  name: string;
  points: number;
  receiptId: number;
};

type allNotificationsExtraDetails = {
  redeemedReferral?: redeemedReferralNotficationDetails[];
};

export type allCutomerNotificationsType = {
  [key in NOTIFICATION_TYPES]?: allNotificationsExtraDetails;
};

export type toggleNotificationReqBody = {
  type: NOTIFICATION_TYPES;
  restaurantId: number;
  notificationIds?: number[];
};

export type userOffersRes = {
  data: {
    offers: OfferType[];
    isFirstTimeOrder: boolean;
    isTwistOfferAvailable: boolean;
  };
};

enum OUT_OF_OPERATION_ENUM {
  BUSY_MODE = "BUSY_MODE",
  IS_OUT_OF_OPERATION_TILL = "IS_OUT_OF_OPERATION_TILL",
  OUTSIDE_OPERATING_HOURS = "OUTSIDE_OPERATING_HOURS",
}

export type franchiseIsOpenRes = {
  isFranchiseOpen: boolean;
  isFranchiseDeliveryOpen: boolean;
  reason?: OUT_OF_OPERATION_ENUM;
  deliveryReason?: OUT_OF_OPERATION_ENUM;
};

export enum PAGER_DUTY_ERROR_URGENCY {
  HIGH = "high",
  LOW = "low",
}

export type ReceiptExtras = {
  hasTwistOffer?: boolean;
  isRoboostReceipt?: boolean;
  source_type?: string;
  deliveryCosts?: number;
  service?: number;
  subTotal?: number;
  totalDiscount?: number;
  vat?: number;
  // ----------------------Currency the redemption took off the order; sits beside the other totals because paid_price is already net of it-----------------------
  pointsDiscountAmount?: number;
  loyaltyPoints?: {
    earned?: { base?: number; bonus?: number; total?: number };
    redeemed?: {
      payWithPoints?: number;
      milestoneOffers?: number;
      total?: number;
      payWithPointsAmount?: number;
    };
  };
};

export type PhoneNumberValidationResult = {
  isValid: boolean;
  countryCode: string | null;
  countryCallingCode: string | null;
  formattedNumber?: string;
};

export type tokenPayload = {
  person_id: number;
  visitor: boolean;
  customer_id: string;
  person_name: string;
  phone_number: string;
  isMobileAppUser: boolean;
  isPartnerAppUser: boolean;
  isWebSelfServiceUser: boolean;
  iat: number;
};

export interface StoreRedirectParams {
  os: string;
  restaurantId: number;
  linkSource?: string;
  deviceId?: string;
}

interface AppleWalletConfig {
  backgroundColor: string;
  foregroundColor: string;
  labelColor: string;
  stripImageUrl?: string;
  logoUrl?: string;
}

interface GoogleWalletConfig {
  bgColor: string;
  logoUrl?: string;
  stripImageUrl?: string;
}

export interface WalletUIConfigs {
  apple?: AppleWalletConfig;
  google?: GoogleWalletConfig;
}
